// compare-chrome — the isolated "Compare algorithms" surface (compare-activity, Trello m98B2mq7).
// Host chrome AROUND the unchanged runner; editor-core and editor-api are untouched and it
// reaches the hidden runner only through hidden-runner.js's public API.
//
//   wire(ctx) — ctx = { core, payload, trinketId, clientsideMode, isOwner, editorApi, doc }
//
// The flow (ledger §I.3):
//   1. PREDICT — the author's questions (if any) are answered and saved first; Run, the growth
//      table and the judge form stay inert until then. The answer is NOT shown yet.
//   2. RUN — every version runs at the chosen size in the HIDDEN runner (one generated driver
//      program per version per action — compare-harness.js), each panel showing its counted
//      steps. The first completed run also checks each version against the author's hidden tests
//      (the server compares; only pass counts come back) and reveals the predict answers.
//   3. GROWTH TABLE (optional) — one run per version covers every configured size.
//   4. JUDGE — four rows (correctness, clarity, efficiency, suitability), a choice and a reason
//      each, submitted ONCE. The reply shows each row's result and the author's model reasons.
//
// LAYOUT (compare-frozen-code-tabs): the problem and every version's code + result stay FROZEN at
// the top (.cmp-stage); the four steps are tabs in the tray under them (.cmp-work), each unlocking
// as the student progresses. Every step keeps its own section element, so nothing below the
// skeleton knows it is a tab.
//
// Staff and a server-resolved preview receive `compareKey` in the config and evaluate the whole
// flow HERE, posting nothing. Every decision lives in the pure compare-logic.js; this file owns
// the DOM, the runner calls and the fetches. one-tab-same-tab-launches (Trello bD1oe35m): a
// SUCCESSFUL student judge submit offers the same-tab countdown back to the lesson (never for
// staff, a local evaluation or a preview, never after a failed request, never on load).

import ace from 'ace-builds/src-noconflict/ace';
import { createHiddenRunner, waitForReady } from './hidden-runner.js';
import {
  COUNT_CAP, RUN_EVENT_CAP, CHECK_EVENT_CAP, MAX_SIZE,
  buildDriver, parseResultLine, parseResults, createLineAssembler, runTimeoutMs,
  countedLines, argsForSize, parseCustomList
} from './compare-harness.js';
import {
  ROWS, checkEntry, localBadges, badgeText, metricLabelFor, mixedMetrics, MIXED_METRIC_NOTICE,
  allowedChoices, rowResultText, localJudge, judgeFormErrors,
  predictGateOpen, runEnabled, judgeEnabled, panelState,
  growthTable, stepsText, stepsLabel, sizeText, nextSalvageAction,
  sweepCell, historySteps, isErrorCell, growthCellText
} from './compare-logic.js';
import { mountPreviewStrip } from './preview-strip.js';
import { inlineMarkdown } from './inline-markdown.js';
import { offerClose } from './close-prompt.js';

var MAX_SENT_RUNS = 50;
var ROW_TEXT = {
  correctness: { q: 'Which versions are correct?', hint: 'Correct means it gives the right answer for every input, not just the tests you saw.' },
  clarity: { q: 'Which version is clearer to read?', hint: 'Think about which one a classmate would understand fastest.' },
  efficiency: { q: 'Which version takes fewer steps?', hint: 'Use the step counts and the growth table, not a guess.' },
  suitability: { q: 'Which version would you choose here?', hint: '' }
};

function readConfig(doc) {
  var el = doc.getElementById('compare-config');
  if (!el) { return null; }
  try {
    var cfg = JSON.parse(el.textContent || '{}');
    return (cfg && typeof cfg === 'object') ? cfg : null;
  } catch (e) { return null; }
}

function el(doc, tag, cls, text) {
  var n = doc.createElement(tag);
  if (cls) { n.className = cls; }
  if (text !== undefined && text !== null) { n.textContent = text; }
  return n;
}

function displayName(label) {
  var l = String(label || '');
  return l.length <= 2 ? 'Version ' + l : l;
}

function safeStorage(fn) {
  try { return fn(window.localStorage); } catch (e) { return null; }
}

export function wire(ctx) {
  var doc = ctx.doc;
  var core = ctx.core;
  var cfg = readConfig(doc);
  if (!cfg || !cfg.compare) { return; }
  var panelHost = doc.getElementById('compare-panel');
  if (!panelHost) { return; }

  var previewMode = cfg.preview === true;
  // one-tab-same-tab-launches: the activity top bar's preview chip and "Back to class" replace
  // the strip, so it is mounted only when the bar is absent (card 2's guard).
  if (!doc.getElementById('surface-topbar-back')) {
    mountPreviewStrip(doc, { active: previewMode, exit: 'navigate', exitUrl: cfg.returnUrl || '', reloadFallback: true });
  }

  var compare = cfg.compare;
  var key = cfg.compareKey || null;
  var local = cfg.local === true;
  var versions = Array.isArray(compare.versions) ? compare.versions : [];
  var tests = Array.isArray(compare.tests) ? compare.tests : [];
  var questions = Array.isArray(compare.predict) ? compare.predict : [];
  var input = compare.input || {};
  var kind = input.kind === 'number' ? 'number' : 'list';
  var presets = (Array.isArray(input.presets) ? input.presets : []).filter(function (n) { return Number.isInteger(n) && n >= 1 && n <= MAX_SIZE; });
  var sweepSizes = (Array.isArray(compare.sweep) ? compare.sweep : []).filter(function (n) { return Number.isInteger(n) && n >= 1 && n <= MAX_SIZE; });
  var testMode = compare.testMode === 'stdout' ? 'stdout' : 'return';
  var marked = versions.map(function (v) { return countedLines(v.code).length > 0; });
  var draftKey = 'compare-draft:' + cfg.materialId + ':' + (cfg.linkedCourseId || 'none');

  // ── state ──────────────────────────────────────────────────────────────────────────────
  var earned = cfg.earned || null;
  var S = {
    choices: questions.map(function () { return ''; }),
    predictionsRecorded: false,
    predictAnswers: null,         // known (from a check reply / the key) but maybe not shown yet
    revealShown: false,
    runsCompleted: 0,
    sessionRuns: [],
    busy: false,
    submitted: false,
    submittedView: null,
    judgeAnswers: null,
    modelReasons: null,
    badges: null,
    checkDone: false,
    checkState: '',               // '' | 'checking' | 'failed'
    sweepDone: false,
    panels: versions.map(function () { return { loading: false, result: null, size: null }; }),
    sweepResults: versions.map(function () { return {}; }),
    judge: {}
  };
  ROWS.forEach(function (row) { S.judge[row] = { choice: '', reason: '' }; });

  if (earned && Array.isArray(earned.predictions)) {
    earned.predictions.forEach(function (p) {
      if (p && Number.isInteger(p.q) && p.q >= 0 && p.q < questions.length) { S.choices[p.q] = p.choice; }
    });
    S.predictionsRecorded = earned.predictions.length > 0;
  }
  if (earned && earned.myResponse && earned.myResponse.correct === true) {
    S.submitted = true;
    S.submittedView = earned.myResponse;
    S.judgeAnswers = earned.judgeAnswers || null;
    S.modelReasons = earned.modelReasons || null;
    S.predictAnswers = Array.isArray(earned.predictAnswers) ? earned.predictAnswers : null;
    S.revealShown = true;
    ROWS.forEach(function (row) {
      var j = (earned.myResponse.judge || {})[row] || {};
      S.judge[row] = { choice: j.choice || '', reason: j.reason || '' };
    });
  } else {
    var draft = safeStorage(function (ls) { return JSON.parse(ls.getItem(draftKey) || 'null'); });
    if (draft && typeof draft === 'object') {
      ROWS.forEach(function (row) {
        var d = draft[row] || {};
        S.judge[row] = { choice: typeof d.choice === 'string' ? d.choice : '', reason: typeof d.reason === 'string' ? d.reason.slice(0, 2000) : '' };
      });
    }
  }

  // ── skeleton ───────────────────────────────────────────────────────────────────────────
  var inner = el(doc, 'div', 'cmp-inner');
  panelHost.appendChild(inner);

  var live = el(doc, 'p', 'cmp-sr');
  live.setAttribute('aria-live', 'polite');
  live.setAttribute('role', 'status');
  inner.appendChild(live);
  function announce(text) { live.textContent = ''; setTimeout(function () { live.textContent = text; }, 30); }

  // The frozen stage (problem + versions) and the tabbed tray of steps under it.
  var stage = el(doc, 'div', 'cmp-stage');
  inner.appendChild(stage);
  var work = el(doc, 'div', 'cmp-work');
  inner.appendChild(work);
  var tabbar = el(doc, 'div', 'cmp-tabbar');
  tabbar.setAttribute('role', 'tablist');
  tabbar.setAttribute('aria-label', 'Steps');
  work.appendChild(tabbar);
  var tabHint = el(doc, 'p', 'cmp-tab-hint');
  tabHint.hidden = true;
  work.appendChild(tabHint);

  // Header: the problem.
  var head = el(doc, 'section', 'cmp-head-row');
  head.appendChild(el(doc, 'h1', 'cmp-sr', cfg.name || 'Compare algorithms'));
  if (compare.problem) {
    // code-a-game-browser-fixes (B): authored prose, escape-first inline markdown (never marked).
    var problemEl = el(doc, 'p', 'cmp-problem');
    problemEl.innerHTML = inlineMarkdown(compare.problem);
    head.appendChild(problemEl);
  }
  if (!compare.problem) {
    head.appendChild(el(doc, 'p', 'cmp-note',
      'Each version below solves the same problem. Run them to count their steps, then decide which is best — and say why.'));
  }
  if (local && !previewMode) {
    head.appendChild(el(doc, 'p', 'cmp-preview-note', 'You are viewing this as a teacher. Nothing you do here is saved.'));
  }
  stage.appendChild(head);

  var runnerError = el(doc, 'p', 'cmp-error');
  runnerError.hidden = true;
  stage.appendChild(runnerError);

  // Predict.
  var predictSection = el(doc, 'section', 'cmp-section');
  var predictFieldsets = [];
  var predictBtn, predictStatus, revealEl;
  if (questions.length) {
    predictSection.appendChild(el(doc, 'h2', 'cmp-sr', 'First, predict'));
    predictSection.appendChild(el(doc, 'p', 'cmp-note', 'Make your guess before you run anything. You will find out if you were right after your first run.'));
    questions.forEach(function (q, qi) {
      var fs = el(doc, 'fieldset', 'cmp-fieldset');
      var lg = el(doc, 'legend', 'cmp-legend', q.q || ('Question ' + (qi + 1)));
      fs.appendChild(lg);
      var choices = el(doc, 'div', 'cmp-choices');
      (q.options || []).forEach(function (opt) {
        var lab = el(doc, 'label', 'cmp-choice');
        var r = doc.createElement('input');
        r.type = 'radio';
        r.name = 'cmp-predict-' + qi;
        r.value = opt;
        r.checked = S.choices[qi] === opt;
        r.addEventListener('change', function () { S.choices[qi] = opt; update(); });
        lab.appendChild(r);
        lab.appendChild(doc.createTextNode(opt));
        choices.appendChild(lab);
      });
      fs.appendChild(choices);
      predictFieldsets.push(fs);
      predictSection.appendChild(fs);
    });
    var pActions = el(doc, 'div', 'cmp-actions');
    predictBtn = el(doc, 'button', 'cmp-btn cmp-btn-primary', 'Save my prediction');
    predictBtn.type = 'button';
    pActions.appendChild(predictBtn);
    predictSection.appendChild(pActions);
    predictStatus = el(doc, 'p', 'cmp-status');
    predictSection.appendChild(predictStatus);
    // The reveal is shown in the Run tab (where the student is when the answer arrives).
    revealEl = el(doc, 'p', 'cmp-reveal');
    revealEl.hidden = true;
    work.appendChild(predictSection);
  }

  // Run controls.
  var runSection = el(doc, 'section', 'cmp-section');
  runSection.appendChild(el(doc, 'h2', 'cmp-sr', 'Run the versions'));
  var controls = el(doc, 'div', 'cmp-controls');
  var sizeField = el(doc, 'label', 'cmp-field', kind === 'number' ? 'Number (n)' : 'List size (n)');
  var sizeInput = doc.createElement('input');
  sizeInput.type = 'number';
  sizeInput.min = '1';
  sizeInput.max = String(MAX_SIZE);
  sizeInput.step = '1';
  sizeInput.className = 'cmp-input';
  sizeInput.value = String(presets[0] || sweepSizes[0] || 10);
  sizeField.appendChild(sizeInput);
  controls.appendChild(sizeField);
  var presetBtns = [];
  if (presets.length) {
    var presetWrap = el(doc, 'div', 'cmp-presets');
    presetWrap.setAttribute('role', 'group');
    presetWrap.setAttribute('aria-label', 'Preset sizes');
    presets.forEach(function (n) {
      var b = el(doc, 'button', 'cmp-btn', 'n = ' + sizeText(n));
      b.type = 'button';
      b.addEventListener('click', function () { sizeInput.value = String(n); update(); });
      presetBtns.push({ btn: b, n: n });
      presetWrap.appendChild(b);
    });
    controls.appendChild(presetWrap);
  }
  runSection.appendChild(controls);
  var customInput = null, customError = null;
  if (kind === 'list') {
    var customField = el(doc, 'label', 'cmp-field', 'Or use your own list (optional)');
    customInput = doc.createElement('input');
    customInput.type = 'text';
    customInput.className = 'cmp-input cmp-input-wide';
    customInput.placeholder = 'For example: 4, 9, 2, 7';
    customInput.setAttribute('autocomplete', 'off');
    customInput.setAttribute('spellcheck', 'false');
    customField.appendChild(customInput);
    runSection.appendChild(customField);
    customError = el(doc, 'p', 'cmp-error');
    customError.id = 'cmp-custom-error';
    customError.hidden = true;
    customInput.setAttribute('aria-describedby', 'cmp-custom-error');
    runSection.appendChild(customError);
    customInput.addEventListener('input', update);
  }
  sizeInput.addEventListener('input', update);
  var runActions = el(doc, 'div', 'cmp-actions');
  var runBtn = el(doc, 'button', 'cmp-btn cmp-btn-run', 'Run all versions');
  runBtn.type = 'button';
  runActions.appendChild(runBtn);
  var runStatus = el(doc, 'p', 'cmp-status');
  runActions.appendChild(runStatus);
  // Run sits on the size row (so the tab needs no scrolling); its Next follows it.
  var runNextBtn = el(doc, 'button', 'cmp-btn cmp-btn-primary cmp-next');
  runNextBtn.type = 'button';
  runNextBtn.hidden = true;
  runActions.appendChild(runNextBtn);
  controls.appendChild(runActions);
  // What each version printed / returned lives here, not in the frozen cards.
  var outGrid = el(doc, 'div', 'cmp-printed-grid');
  outGrid.hidden = true;
  runSection.appendChild(outGrid);
  if (revealEl) { runSection.appendChild(revealEl); }
  work.appendChild(runSection);

  // Version panels.
  var mixedNotice = el(doc, 'p', 'cmp-notice', MIXED_METRIC_NOTICE);
  mixedNotice.hidden = !mixedMetrics(marked);
  runSection.appendChild(mixedNotice);
  var grid = el(doc, 'div', 'cmp-versions');
  grid.style.setProperty('--cmp-cols', String(Math.max(1, versions.length)));
  var panelEls = versions.map(function (v, vi) {
    var card = el(doc, 'article', 'cmp-version');
    var vh = el(doc, 'div', 'cmp-version-head');
    var title = el(doc, 'h3', 'cmp-version-title', displayName(v.label));
    title.id = 'cmp-version-' + vi;
    vh.appendChild(title);
    vh.appendChild(el(doc, 'span', 'cmp-metric', metricLabelFor(compare, marked[vi])));
    card.appendChild(vh);
    card.setAttribute('aria-labelledby', title.id);
    var pre = el(doc, 'pre', 'cmp-code');
    pre.tabIndex = 0;
    pre.setAttribute('aria-label', 'Code for ' + displayName(v.label));
    var counted = countedLines(v.code);
    String(v.code || '').replace(/\r\n?/g, '\n').replace(/\n+$/, '').split('\n').forEach(function (line, i) {
      var row = el(doc, 'span', 'cmp-line');
      row.appendChild(el(doc, 'span', 'cmp-ln', String(i + 1)));
      row.firstChild.setAttribute('aria-hidden', 'true');
      row.appendChild(el(doc, 'span', 'cmp-src', line));
      if (counted.indexOf(i + 1) !== -1) {
        row.classList.add('is-counted');
        row.appendChild(el(doc, 'span', 'cmp-count-tag', 'counted'));
      }
      pre.appendChild(row);
    });
    card.appendChild(pre);
    var outHost = el(doc, 'div', 'cmp-printed');
    outHost.hidden = true;
    outGrid.appendChild(outHost);
    var foot = el(doc, 'div', 'cmp-foot');
    var result = el(doc, 'div', 'cmp-result');
    foot.appendChild(result);
    var badge = el(doc, 'p', 'cmp-badge');
    badge.hidden = true;
    foot.appendChild(badge);
    vh.appendChild(foot);
    grid.appendChild(card);
    return { result: result, badge: badge, out: outHost, name: displayName(v.label) };
  });
  stage.appendChild(grid);

  // Growth table.
  var sweepSection = null, sweepBtn = null, sweepStatus = null, tableHost = null, sweepNextBtn = null, sweepNote = null;
  if (sweepSizes.length) {
    sweepSection = el(doc, 'section', 'cmp-section');
    sweepSection.appendChild(el(doc, 'h2', 'cmp-label', 'How do the steps grow?'));
    sweepNote = el(doc, 'p', 'cmp-note',
      'Run every version at each size in the table to see how its steps change as the ' + (kind === 'number' ? 'number' : 'list') + ' gets bigger.');
    sweepSection.appendChild(sweepNote);
    var sActions = el(doc, 'div', 'cmp-actions');
    sweepBtn = el(doc, 'button', 'cmp-btn cmp-btn-primary', 'Fill in the growth table');
    sweepBtn.type = 'button';
    sActions.appendChild(sweepBtn);
    sweepStatus = el(doc, 'p', 'cmp-status');
    sActions.appendChild(sweepStatus);
    sweepNextBtn = el(doc, 'button', 'cmp-btn cmp-btn-primary cmp-next', 'Next: judge the versions');
    sweepNextBtn.type = 'button';
    sweepNextBtn.hidden = true;
    sActions.appendChild(sweepNextBtn);
    sweepSection.appendChild(sActions);
    tableHost = el(doc, 'div', 'cmp-section');
    sweepSection.appendChild(tableHost);
    work.appendChild(sweepSection);
  }

  // Judge form.
  var judgeSection = el(doc, 'section', 'cmp-section');
  var judgeHeading = el(doc, 'h2', 'cmp-sr', 'Judge the versions');
  judgeHeading.tabIndex = -1;
  judgeSection.appendChild(judgeHeading);
  var judgeIntro = el(doc, 'p', 'cmp-note', 'Choose an answer for each question and give a reason. You can submit once.');
  judgeSection.appendChild(judgeIntro);
  var judgeGrid = el(doc, 'div', 'cmp-judge-grid');
  judgeSection.appendChild(judgeGrid);
  var allowed = allowedChoices(compare);
  var judgeRows = {};
  ROWS.forEach(function (row) {
    var fs = el(doc, 'fieldset', 'cmp-fieldset');
    var lg = el(doc, 'legend', 'cmp-legend', ROW_TEXT[row].q);
    lg.id = 'cmp-legend-' + row;
    var hint = row === 'suitability'
      ? ((compare.judge && compare.judge.suitability && compare.judge.suitability.scenario) || '')
      : ROW_TEXT[row].hint;
    if (hint) { lg.appendChild(el(doc, 'small', null, hint)); }
    fs.appendChild(lg);
    var choices = el(doc, 'div', 'cmp-choices');
    var radios = [];
    allowed[row].forEach(function (opt) {
      var lab = el(doc, 'label', 'cmp-choice');
      var r = doc.createElement('input');
      r.type = 'radio';
      r.name = 'cmp-judge-' + row;
      r.value = opt;
      r.checked = S.judge[row].choice === opt;
      r.addEventListener('change', function () { S.judge[row].choice = opt; saveDraft(); update(); });
      radios.push(r);
      lab.appendChild(r);
      lab.appendChild(doc.createTextNode(opt));
      choices.appendChild(lab);
    });
    fs.appendChild(choices);
    var reasonLabel = el(doc, 'label', 'cmp-field', 'Your reason');
    reasonLabel.setAttribute('for', 'cmp-reason-' + row);
    fs.appendChild(reasonLabel);
    var ta = doc.createElement('textarea');
    ta.className = 'cmp-textarea compare-reason';
    ta.id = 'cmp-reason-' + row;
    ta.maxLength = 500;
    ta.value = S.judge[row].reason;
    ta.setAttribute('aria-labelledby', lg.id + ' ' + 'cmp-reason-label-' + row);
    reasonLabel.id = 'cmp-reason-label-' + row;
    var err = el(doc, 'p', 'cmp-error');
    err.id = 'cmp-reason-error-' + row;
    err.hidden = true;
    ta.setAttribute('aria-describedby', err.id);
    ta.addEventListener('input', function () { S.judge[row].reason = ta.value; saveDraft(); });
    fs.appendChild(ta);
    fs.appendChild(err);
    var resultEl = el(doc, 'p', 'cmp-row-result');
    resultEl.hidden = true;
    fs.appendChild(resultEl);
    var modelEl = el(doc, 'div', 'cmp-section');
    modelEl.hidden = true;
    fs.appendChild(modelEl);
    judgeGrid.appendChild(fs);
    judgeRows[row] = { fieldset: fs, radios: radios, textarea: ta, error: err, result: resultEl, model: modelEl };
  });
  // One question at a time (so the tab never needs a scrollbar); after the submit every question
  // and its result are shown together.
  var judgeStep = 0;
  var judgeProgress = el(doc, 'p', 'cmp-judge-progress');
  judgeSection.insertBefore(judgeProgress, judgeGrid);
  var jActions = el(doc, 'div', 'cmp-actions');
  var judgeBackBtn = el(doc, 'button', 'cmp-btn', 'Back');
  judgeBackBtn.type = 'button';
  var judgeNextBtn = el(doc, 'button', 'cmp-btn cmp-btn-primary', 'Next question');
  judgeNextBtn.type = 'button';
  var submitBtn = el(doc, 'button', 'cmp-btn cmp-btn-primary', 'Submit my answers');
  submitBtn.type = 'button';
  jActions.appendChild(judgeBackBtn);
  jActions.appendChild(judgeNextBtn);
  jActions.appendChild(submitBtn);
  function renderJudgeStep() {
    var stepping = !S.submitted;
    judgeGrid.classList.toggle('is-stepping', stepping);
    ROWS.forEach(function (row, i) { judgeRows[row].fieldset.hidden = stepping && i !== judgeStep; });
    judgeProgress.hidden = !stepping;
    judgeProgress.textContent = 'Question ' + (judgeStep + 1) + ' of ' + ROWS.length + ' — pick an answer and give a reason. You can submit once.';
    judgeIntro.hidden = stepping;
    judgeBackBtn.hidden = !stepping || judgeStep === 0;
    judgeNextBtn.hidden = !stepping || judgeStep === ROWS.length - 1;
    submitBtn.hidden = S.submitted || judgeStep !== ROWS.length - 1;
  }
  function showJudgeStep(i) {
    judgeStep = Math.max(0, Math.min(ROWS.length - 1, i));
    renderJudgeStep();
  }
  judgeBackBtn.addEventListener('click', function () {
    showJudgeStep(judgeStep - 1);
    judgeRows[ROWS[judgeStep]].radios[0].focus();
  });
  judgeNextBtn.addEventListener('click', function () {
    showJudgeStep(judgeStep + 1);
    judgeRows[ROWS[judgeStep]].radios[0].focus();
  });
  var judgeStatus = el(doc, 'p', 'cmp-status');
  jActions.appendChild(judgeStatus);
  judgeSection.appendChild(jActions);
  var judgeError = el(doc, 'p', 'cmp-error');
  judgeError.hidden = true;
  judgeSection.appendChild(judgeError);
  var resultsBlock = el(doc, 'div', 'cmp-section');
  resultsBlock.hidden = true;
  judgeSection.appendChild(resultsBlock);
  work.appendChild(judgeSection);

  // ── the step tabs ──────────────────────────────────────────────────────────────────────
  // Predict (only when authored) · Run · Growth (only when sizes are configured) · Judge. A tab
  // opens as its gate does (the same predicates that enable its controls); a locked tab stays
  // focusable and says why when pressed. Finished steps carry a check.
  var SVG_NS = 'http://www.w3.org/2000/svg';
  function markSvg(kind) {
    var svg = doc.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    var shapes = kind === 'lock'
      ? [['rect', { x: 5, y: 11, width: 14, height: 9, rx: 2 }], ['path', { d: 'M8 11V8a4 4 0 0 1 8 0v3' }]]
      : [['path', { d: 'M5 12.5l4.5 4.5L19 7.5' }]];
    shapes.forEach(function (sh) {
      var n = doc.createElementNS(SVG_NS, sh[0]);
      Object.keys(sh[1]).forEach(function (k) { n.setAttribute(k, String(sh[1][k])); });
      svg.appendChild(n);
    });
    return svg;
  }
  function predicted() { return questions.length === 0 || S.predictionsRecorded; }
  var TABS = [];
  var activeTab = null;
  function addTab(id, label, panel, isLocked, isDone, lockedReason) {
    var btn = el(doc, 'button', 'cmp-tab');
    btn.type = 'button';
    btn.id = 'cmp-tab-' + id;
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-controls', 'cmp-tabpanel-' + id);
    var mark = el(doc, 'span', 'cmp-tab-mark');
    mark.setAttribute('aria-hidden', 'true');
    btn.appendChild(mark);
    btn.appendChild(doc.createTextNode(label));
    var sr = el(doc, 'span', 'cmp-sr');
    btn.appendChild(sr);
    panel.id = 'cmp-tabpanel-' + id;
    panel.classList.add('cmp-tabpanel');
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('aria-labelledby', btn.id);
    var tab = { id: id, btn: btn, panel: panel, mark: mark, sr: sr, isLocked: isLocked, isDone: isDone, reason: lockedReason };
    btn.addEventListener('click', function () {
      if (tab.isLocked()) { tabHint.textContent = tab.reason; tabHint.hidden = false; announce(tab.reason); return; }
      selectTab(id);
    });
    btn.addEventListener('keydown', function (ev) {
      var i = TABS.indexOf(tab);
      var to = ev.key === 'ArrowRight' ? i + 1 : ev.key === 'ArrowLeft' ? i - 1 : ev.key === 'Home' ? 0 : ev.key === 'End' ? TABS.length - 1 : -1;
      if (to < 0) { return; }
      ev.preventDefault();
      TABS[(to + TABS.length) % TABS.length].btn.focus();
    });
    tabbar.appendChild(btn);
    TABS.push(tab);
    return tab;
  }
  if (questions.length) {
    addTab('predict', 'Predict', predictSection, function () { return false; }, function () { return S.predictionsRecorded; }, '');
  }
  addTab('run', 'Run', runSection, function () { return !predicted(); }, function () { return S.runsCompleted > 0; }, 'Save your prediction first.');
  if (sweepSection) {
    addTab('growth', 'Growth', sweepSection, function () { return !predicted(); }, function () { return S.sweepDone; }, 'Save your prediction first.');
  }
  addTab('judge', 'Judge', judgeSection, function () { return !judgeEnabled(stateForLogic()); }, function () { return S.submitted; }, 'Run the code first, then you can judge.');
  function selectTab(id) {
    var next = null;
    TABS.forEach(function (t) { if (t.id === id) { next = t; } });
    if (!next || next.isLocked()) { return; }
    activeTab = next;
    tabHint.hidden = true;
    renderTabs();
  }
  function renderTabs() {
    TABS.forEach(function (t) {
      var on = t === activeTab;
      var locked = t.isLocked();
      var done = !locked && t.isDone();
      t.btn.setAttribute('aria-selected', String(on));
      t.btn.tabIndex = on ? 0 : -1;
      if (locked) { t.btn.setAttribute('aria-disabled', 'true'); } else { t.btn.removeAttribute('aria-disabled'); }
      t.btn.classList.toggle('is-done', done);
      t.mark.textContent = '';
      if (locked) { t.mark.appendChild(markSvg('lock')); } else if (done) { t.mark.appendChild(markSvg('check')); }
      t.sr.textContent = locked ? ' (locked)' : (done ? ' (done)' : '');
      t.panel.hidden = !on;
    });
  }

  function saveDraft() {
    if (S.submitted) { return; }
    safeStorage(function (ls) { ls.setItem(draftKey, JSON.stringify(S.judge)); });
  }

  // ── rendering ──────────────────────────────────────────────────────────────────────────
  function customList() {
    if (!customInput) { return { ok: true, list: null }; }
    return parseCustomList(customInput.value);
  }

  function liveSize() {
    var c = customList();
    if (c.ok && c.list) { return { size: c.list.length, list: c.list }; }
    var n = Math.floor(Number(sizeInput.value));
    if (!Number.isInteger(n) || n < 1 || n > MAX_SIZE) { return null; }
    return { size: n, list: null };
  }

  function stateForLogic() {
    return {
      questions: questions.length,
      answered: S.choices.filter(Boolean).length,
      predictionsRecorded: S.predictionsRecorded,
      runsCompleted: S.runsCompleted,
      busy: S.busy,
      submitted: S.submitted
    };
  }

  // The Run tab shows the first few lines (no nested scrollbar); the count says what was left out.
  function clipLines(text) {
    var lines = String(text).replace(/\n+$/, '').split('\n');
    if (lines.length <= 4) { return lines.join('\n'); }
    return lines.slice(0, 3).join('\n') + '\n… and ' + (lines.length - 3) + ' more lines';
  }

  function renderPanel(vi) {
    var p = S.panels[vi];
    var host = panelEls[vi].result;
    var outHost = panelEls[vi].out;
    host.textContent = '';
    outHost.textContent = '';
    var st = panelState(p);
    function syncOut() {
      outHost.hidden = !outHost.firstChild;
      outGrid.hidden = !panelEls.some(function (pe) { return !pe.out.hidden; });
    }
    if (st === 'empty') { host.appendChild(el(doc, 'p', 'cmp-placeholder', 'Run to see results')); syncOut(); return; }
    if (st === 'loading') {
      var l = el(doc, 'span', 'cmp-loading');
      var sp = el(doc, 'span', 'cmp-spinner');
      sp.setAttribute('aria-hidden', 'true');
      l.appendChild(sp);
      l.appendChild(doc.createTextNode(p.salvaging ? 'Checking the slower sizes…' : 'Running…'));
      host.appendChild(l);
      syncOut();
      return;
    }
    var r = p.result;
    if (r.status === 'error') {
      host.appendChild(el(doc, 'p', 'cmp-steps', 'This version raised an error'));
      outHost.appendChild(el(doc, 'p', 'cmp-metric', panelEls[vi].name + ' raised:'));
      outHost.appendChild(el(doc, 'pre', 'cmp-out is-error', r.error || 'Error'));
    } else {
      var steps = el(doc, 'p', 'cmp-steps', r.status === 'capped' ? stepsText(null) : stepsLabel(r.steps));
      steps.appendChild(el(doc, 'small', null, '  at n = ' + sizeText(p.size)));
      host.appendChild(steps);
    }
    if (r.output) {
      outHost.appendChild(el(doc, 'p', 'cmp-metric', panelEls[vi].name + ' printed:'));
      outHost.appendChild(el(doc, 'pre', 'cmp-out', clipLines(r.output) + (r.outputTruncated ? '\n… (cut short)' : '')));
    }
    if (r.returned) {
      outHost.appendChild(el(doc, 'p', 'cmp-metric', panelEls[vi].name + ' returned:'));
      outHost.appendChild(el(doc, 'pre', 'cmp-out', r.returned));
    }
    syncOut();
  }

  function renderBadges() {
    versions.forEach(function (v, vi) {
      var b = panelEls[vi].badge;
      b.className = 'cmp-badge';
      b.hidden = false;
      if (S.checkState === 'checking') { b.textContent = 'Checking the tests…'; return; }
      if (S.checkState === 'failed') { b.textContent = 'Couldn’t check — run again to retry'; return; }
      var badge = S.badges && S.badges[vi];
      if (!badge || !badge.total) { b.hidden = true; return; }
      var pass = badge.passed === badge.total;
      b.classList.add(pass ? 'is-pass' : 'is-fail');
      b.textContent = (pass ? '✓ ' : '✗ ') + badgeText(badge);
    });
  }

  function renderReveal() {
    if (!revealEl) { return; }
    if (!S.revealShown || !S.predictAnswers) { revealEl.hidden = true; return; }
    var parts = questions.map(function (q, qi) {
      return 'You predicted ' + (S.choices[qi] || 'nothing') + '; the answer is ' + (S.predictAnswers[qi] || '—') + '.';
    });
    revealEl.textContent = parts.join(' ');
    revealEl.hidden = false;
  }

  function renderTable() {
    if (!tableHost) { return; }
    tableHost.textContent = '';
    var t = growthTable(sweepSizes, versions.length, S.sweepResults, kind);
    var wrap = el(doc, 'div', 'cmp-table-wrap');
    var table = el(doc, 'table', 'cmp-table');
    table.appendChild(el(doc, 'caption', null, 'Steps for each ' + (kind === 'number' ? 'number' : 'list size')));
    var thead = el(doc, 'thead');
    var hr = el(doc, 'tr');
    var th0 = el(doc, 'th', null, kind === 'number' ? 'n' : 'List size');
    th0.scope = 'col';
    hr.appendChild(th0);
    versions.forEach(function (v) {
      var th = el(doc, 'th', null, displayName(v.label));
      th.scope = 'col';
      hr.appendChild(th);
    });
    thead.appendChild(hr);
    table.appendChild(thead);
    var tbody = el(doc, 'tbody');
    t.rows.forEach(function (row) {
      var tr = el(doc, 'tr');
      var th = el(doc, 'th', null, sizeText(row.size));
      th.scope = 'row';
      tr.appendChild(th);
      row.cells.forEach(function (v, i) {
        var td = el(doc, 'td');
        var cell = el(doc, 'div', 'cmp-cell');
        if (isErrorCell(v)) {
          // The version RAISED at this size: show the error text, no bar (never "too many").
          cell.appendChild(el(doc, 'span', 'cmp-cell-num', 'Raised an error'));
          cell.appendChild(el(doc, 'span', 'cmp-cell-error', growthCellText(v)));
          td.appendChild(cell);
          tr.appendChild(td);
          return;
        }
        cell.appendChild(el(doc, 'span', 'cmp-cell-num', growthCellText(v)));
        var bar = el(doc, 'div', 'cmp-bar' + (v === null ? ' is-capped' : ''));
        bar.setAttribute('aria-hidden', 'true');
        var fill = el(doc, 'span');
        fill.style.width = Math.round(row.bars[i] * 100) + '%';
        bar.appendChild(fill);
        cell.appendChild(bar);
        td.appendChild(cell);
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    wrap.appendChild(table);
    tableHost.appendChild(wrap);
    var sentences = t.sentences.map(function (s, i) { return s ? displayName(versions[i].label) + ' ' + s + '.' : ''; }).filter(Boolean);
    if (sentences.length) {
      var ul = el(doc, 'ul', 'cmp-framing');
      sentences.forEach(function (s) { ul.appendChild(el(doc, 'li', null, s)); });
      tableHost.appendChild(ul);
    }
  }

  function renderSubmitted() {
    if (!S.submitted || !S.submittedView) { resultsBlock.hidden = true; return; }
    var view = S.submittedView;
    ROWS.forEach(function (row) {
      var jr = judgeRows[row];
      var j = (view.judge || {})[row] || {};
      jr.result.hidden = false;
      jr.result.className = 'cmp-row-result' + (j.result ? ' is-' + j.result : '');
      jr.result.textContent = rowResultText(j.result);
      jr.model.textContent = '';
      var reason = S.modelReasons && S.modelReasons[row];
      var answer = S.judgeAnswers && S.judgeAnswers[row];
      if (answer && String(answer).toLowerCase() !== 'open') {
        jr.model.appendChild(el(doc, 'p', 'cmp-note', 'The answer key says: ' + answer));
      }
      if (reason) {
        jr.model.appendChild(el(doc, 'p', 'cmp-metric', 'A model reason:'));
        jr.model.appendChild(el(doc, 'p', 'cmp-model', reason));
      }
      jr.model.hidden = !jr.model.firstChild;
    });
    resultsBlock.textContent = '';
    var h = el(doc, 'h2', 'cmp-heading', 'Your judgement is in');
    h.tabIndex = -1;
    h.id = 'cmp-results-heading';
    resultsBlock.appendChild(h);
    if (view.keyedTotal) {
      resultsBlock.appendChild(el(doc, 'p', 'cmp-problem',
        view.keyedCorrect + ' of ' + view.keyedTotal + ' keyed answers match. Compare each of your reasons with the model reason above.'));
    }
    resultsBlock.appendChild(el(doc, 'p', 'cmp-done', 'Done — you made your judgement.'));
    resultsBlock.hidden = false;
    judgeIntro.textContent = 'You have submitted your judgement. You can still run the versions.';
  }

  function update() {
    var st = stateForLogic();
    predictFieldsets.forEach(function (fs) { fs.disabled = S.predictionsRecorded || S.busy; });
    if (predictBtn) {
      predictBtn.hidden = S.predictionsRecorded;
      predictBtn.disabled = S.busy || !predictGateOpen(st);
    }
    var canRun = runEnabled(st) && !runnerFailed;
    var custom = customList();
    var useCustom = custom.ok && !!custom.list;
    if (customError) {
      customError.hidden = custom.ok;
      customError.textContent = custom.ok ? '' : custom.error;
      if (customInput) { customInput.setAttribute('aria-invalid', custom.ok ? 'false' : 'true'); }
    }
    if (useCustom) { sizeInput.value = String(custom.list.length); }
    sizeInput.disabled = !canRun || useCustom;
    presetBtns.forEach(function (p) {
      p.btn.disabled = !canRun || useCustom;
      p.btn.setAttribute('aria-pressed', String(Number(sizeInput.value) === p.n && !useCustom));
    });
    if (customInput) { customInput.disabled = !canRun; }
    runBtn.disabled = !canRun || !custom.ok || !liveSize();
    if (sweepBtn) { sweepBtn.disabled = !canRun; }
    if (!runEnabled(st) && !S.busy && questions.length && !S.predictionsRecorded) {
      runStatus.textContent = 'Save your prediction first.';
    } else if (!S.busy && runStatus.textContent === 'Save your prediction first.') {
      runStatus.textContent = '';
    }
    var canJudge = judgeEnabled(st) && !S.submitted && !S.busy;
    ROWS.forEach(function (row) {
      judgeRows[row].fieldset.disabled = !canJudge;
    });
    submitBtn.disabled = !canJudge;
    renderJudgeStep();
    runNextBtn.dataset.next = sweepSection ? 'growth' : 'judge';
    runNextBtn.textContent = sweepSection ? 'Next: see how the steps grow' : 'Next: judge the versions';
    runNextBtn.hidden = S.runsCompleted < 1 || S.busy;
    if (sweepNextBtn) { sweepNextBtn.hidden = !S.sweepDone || S.busy; }
    if (sweepNote) { sweepNote.hidden = S.sweepDone; }
    renderTabs();
    if (!S.submitted && !judgeEnabled(st)) {
      judgeStatus.textContent = 'You can judge once you have run the code.';
    } else if (!S.submitted) {
      judgeStatus.textContent = '';
    }
  }

  // ── the hidden runner ──────────────────────────────────────────────────────────────────
  var hidden = null;
  var runnerFailed = false;
  var visibleReady = false;
  core.on('ready', function () { visibleReady = true; });

  function ensureHidden() {
    var start = visibleReady ? Promise.resolve() : waitForReady(core).catch(function () {});
    return start.then(function () {
      if (!hidden) { hidden = createHiddenRunner({ doc: doc, ace: ace, mode: 'python', source: '' }); }
      return hidden.ready();
    }).catch(function (err) {
      runnerFailed = true;
      runnerError.textContent = 'The code runner could not start. Reload the page to try again.';
      runnerError.hidden = false;
      update();
      throw err;
    });
  }

  // Run a set of items for ONE version: one driver program for all of them (smallest first),
  // result lines collected from the live stdout stream; on a whole-run timeout the pure
  // `nextSalvageAction` decides what to re-run alone and what to mark "too many to count".
  function runItems(vi, items, eventCap, op) {
    var ids = items.map(function (it) { return it.id; });
    var byId = {};
    items.forEach(function (it) { byId[it.id] = it; });
    var results = {};

    function take(line) {
      var r = parseResultLine(line);
      if (r && ids.indexOf(r.id) !== -1) { results[r.id] = r; }
    }

    function runBatch(batch) {
      var src = buildDriver({
        code: versions[vi].code, functionName: compare.functionName,
        items: batch, eventCap: eventCap, countCap: COUNT_CAP
      });
      var asm = createLineAssembler();
      function onOut(chunk) { asm.push(chunk).forEach(take); }
      hidden.core.on('stdout', onOut);
      return hidden.setSource(src)
        .then(function () { return hidden.consoleRun([], runTimeoutMs(batch.length)); })
        .then(function (res) {
          hidden.core.off('stdout', onOut);
          asm.flush().forEach(take);
          if (res && res.status === 'ok' && res.value && res.value.stdout) {
            var parsed = parseResults(res.value.stdout);
            Object.keys(parsed).forEach(function (id) {
              if (ids.indexOf(id) !== -1 && !results[id]) { results[id] = parsed[id]; }
            });
          }
          return res || { status: 'lost' };
        }, function () {
          hidden.core.off('stdout', onOut);
          return { status: 'error' };
        });
    }

    return runBatch(items).then(function (res) {
      var last = { type: 'run-all', status: res.status };
      function step() {
        var action = nextSalvageAction({
          items: ids, finished: Object.keys(results), elapsedMs: op.salvageMs, lastResult: last
        });
        if (action.type === 'done') { return results; }
        if (op.onSalvage) { op.onSalvage(vi); }
        if (action.type === 'mark-capped') {
          action.items.forEach(function (id) { results[id] = { id: id, status: 'capped', steps: null }; });
          return step();
        }
        var t0 = Date.now();
        return runBatch([byId[action.item]]).then(function (r2) {
          op.salvageMs += Date.now() - t0;
          last = { type: 'rerun', item: action.item, status: r2.status };
          return step();
        });
      }
      return step();
    });
  }

  function newOp() { return { salvageMs: 0, onSalvage: null }; }

  // ── fetches (students only; staff / preview post nothing) ──────────────────────────────
  function respondUrl() {
    return '/api/courses/' + cfg.courseId + '/lessons/' + cfg.lessonId + '/materials/' + cfg.materialId + '/respond';
  }
  function post(answers) {
    var body = { answers: answers };
    if (cfg.linkedCourseId) { body.linkedCourseId = cfg.linkedCourseId; }
    return fetch(respondUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        if (!res.ok) {
          var e = new Error((json && json.message) || ('respond-' + res.status));
          e.status = res.status;
          throw e;
        }
        return json || {};
      });
    });
  }
  function unsentRuns() {
    return S.sessionRuns.slice(-MAX_SENT_RUNS);
  }

  // ── predict ────────────────────────────────────────────────────────────────────────────
  if (predictBtn) {
    predictBtn.addEventListener('click', function () {
      if (predictBtn.disabled) { return; }
      S.busy = true;
      update();
      predictStatus.textContent = 'Saving…';
      var preds = S.choices.map(function (c, qi) { return { q: qi, choice: c }; });
      var send = local ? Promise.resolve({ predictions: preds }) : post({ action: 'predict', predictions: preds, runs: [] });
      send.then(function (reply) {
        if (reply && Array.isArray(reply.predictions) && reply.predictions.length) {
          reply.predictions.forEach(function (p) { if (p && p.q < S.choices.length) { S.choices[p.q] = p.choice; } });
          predictFieldsets.forEach(function (fs, qi) {
            Array.prototype.forEach.call(fs.querySelectorAll('input'), function (r) { r.checked = r.value === S.choices[qi]; });
          });
        }
        S.predictionsRecorded = true;
        if (local && key) { S.predictAnswers = Array.isArray(key.predictAnswers) ? key.predictAnswers : null; }
        predictStatus.textContent = 'Prediction saved — run the code to find out.';
        announce('Prediction saved. Run the code to find out.');
      }).catch(function (err) {
        console.error(err);
        predictStatus.textContent = 'Your prediction could not be saved. Check your connection and try again.';
      }).then(function () {
        S.busy = false;
        if (S.predictionsRecorded) { selectTab('run'); }
        update();
        if (S.predictionsRecorded) { runBtn.focus(); }
      });
    });
  }

  // ── run / check ────────────────────────────────────────────────────────────────────────
  function doCheck() {
    if (S.checkDone || !tests.length) { return Promise.resolve(); }
    S.checkState = 'checking';
    renderBadges();
    var list = [];
    var chain = Promise.resolve();
    versions.forEach(function (v, vi) {
      chain = chain.then(function () {
        var items = tests.map(function (t, ti) { return { id: 't' + ti, args: Array.isArray(t.args) ? t.args : [] }; });
        return runItems(vi, items, CHECK_EVENT_CAP, newOp()).then(function (res) {
          list.push({ version: vi, tests: tests.map(function (t, ti) { return checkEntry(res['t' + ti], testMode); }) });
        });
      });
    });
    return chain.then(function () {
      if (local) {
        return { badges: localBadges(compare, key, list), predictAnswers: key && key.predictAnswers };
      }
      return post({ action: 'check', check: { results: list } });
    }).then(function (reply) {
      S.badges = Array.isArray(reply && reply.badges) ? reply.badges : null;
      if (reply && Array.isArray(reply.predictAnswers) && S.predictionsRecorded) { S.predictAnswers = reply.predictAnswers; }
      S.checkDone = !!S.badges;
      S.checkState = S.checkDone ? '' : 'failed';
      if (S.badges) {
        S.badges.forEach(function (b, vi) {
          announce(displayName(versions[vi].label) + ': ' + badgeText(b) + '.');
        });
      }
    }).catch(function (err) {
      console.error(err);
      S.checkState = 'failed';
    }).then(renderBadges);
  }

  function recordRun(size, stepsPerVersion) {
    S.sessionRuns.push({ size: size, steps: stepsPerVersion });
    if (S.sessionRuns.length > 200) { S.sessionRuns = S.sessionRuns.slice(-MAX_SENT_RUNS); }
  }

  runBtn.addEventListener('click', function () {
    if (runBtn.disabled) { return; }
    var ls = liveSize();
    if (!ls) { return; }
    S.busy = true;
    update();
    runStatus.textContent = 'Running…';
    var args = ls.list ? [ls.list] : argsForSize(input, ls.size);
    var steps = [];
    ensureHidden().then(function () {
      var chain = Promise.resolve();
      versions.forEach(function (v, vi) {
        chain = chain.then(function () {
          S.panels[vi] = { loading: true, result: null, size: ls.size };
          renderPanel(vi);
          var op = newOp();
          op.onSalvage = function () { S.panels[vi].salvaging = true; renderPanel(vi); runStatus.textContent = 'Checking the slower sizes…'; };
          return runItems(vi, [{ id: 'live', args: args }], RUN_EVENT_CAP, op).then(function (res) {
            var r = res.live || { status: 'capped', steps: null };
            S.panels[vi] = { loading: false, result: r, size: ls.size };
            steps[vi] = historySteps(sweepCell(r));
            renderPanel(vi);
            announce(displayName(v.label) + ': ' + (r.status === 'error' ? 'raised an error' : (r.status === 'capped' ? 'too many steps to count' : stepsLabel(r.steps))) + '.');
          });
        });
      });
      return chain;
    }).then(function () {
      recordRun(ls.size, steps);
      S.runsCompleted++;
      runStatus.textContent = '';
      return doCheck();
    }).then(function () {
      if (S.predictAnswers && S.predictionsRecorded && !S.revealShown) {
        S.revealShown = true;
        renderReveal();
        announce(revealEl ? revealEl.textContent : '');
      }
    }).catch(function (err) {
      console.error(err);
      if (!runnerFailed) { runStatus.textContent = 'Something went wrong running the code. Try again.'; }
      S.panels.forEach(function (p, vi) { if (p.loading) { S.panels[vi] = { loading: false, result: null, size: null }; renderPanel(vi); } });
    }).then(function () {
      S.busy = false;
      update();
    });
  });

  if (sweepBtn) {
    sweepBtn.addEventListener('click', function () {
      if (sweepBtn.disabled) { return; }
      S.busy = true;
      update();
      var sizes = sweepSizes.slice().sort(function (a, b) { return a - b; });
      ensureHidden().then(function () {
        var chain = Promise.resolve();
        versions.forEach(function (v, vi) {
          chain = chain.then(function () {
            sweepStatus.textContent = 'Measuring ' + displayName(v.label) + '…';
            var op = newOp();
            op.onSalvage = function () { sweepStatus.textContent = 'Checking the slower sizes…'; };
            var items = sizes.map(function (n) { return { id: 's' + n, args: argsForSize(input, n) }; });
            return runItems(vi, items, RUN_EVENT_CAP, op).then(function (res) {
              sizes.forEach(function (n) {
                var r = res['s' + n];
                S.sweepResults[vi][n] = sweepCell(r);
              });
              renderTable();
              var raised = sizes.some(function (n) { return isErrorCell(S.sweepResults[vi][n]); });
              announce(displayName(v.label) + (raised ? ' raised an error at some sizes.' : ' measured at every size.'));
            });
          });
        });
        return chain;
      }).then(function () {
        sizes.forEach(function (n) {
          recordRun(n, versions.map(function (v, vi) { return historySteps(S.sweepResults[vi][n]); }));
        });
        S.runsCompleted++;
        S.sweepDone = true;
        sweepStatus.textContent = 'The growth table is filled in.';
        return doCheck();
      }).then(function () {
        if (S.predictAnswers && S.predictionsRecorded && !S.revealShown) {
          S.revealShown = true;
          renderReveal();
        }
      }).catch(function (err) {
        console.error(err);
        if (!runnerFailed) { sweepStatus.textContent = 'Something went wrong running the code. Try again.'; }
      }).then(function () {
        S.busy = false;
        update();
      });
    });
  }

  // ── submit ─────────────────────────────────────────────────────────────────────────────
  submitBtn.addEventListener('click', function () {
    if (submitBtn.disabled) { return; }
    var errs = judgeFormErrors(compare, S.judge);
    var first = null;
    ROWS.forEach(function (row) {
      var e = judgeRows[row].error;
      e.hidden = !errs[row];
      e.textContent = errs[row] || '';
      judgeRows[row].textarea.setAttribute('aria-invalid', errs[row] ? 'true' : 'false');
      if (errs[row] && !first) { first = row; }
    });
    if (first) {
      showJudgeStep(ROWS.indexOf(first));
      var jr = judgeRows[first];
      var target = S.judge[first].choice ? jr.textarea : (jr.radios[0] || jr.textarea);
      target.focus();
      return;
    }
    S.busy = true;
    update();
    judgeError.hidden = true;
    judgeStatus.textContent = 'Sending…';
    var judge = {};
    ROWS.forEach(function (row) { judge[row] = { choice: S.judge[row].choice, reason: S.judge[row].reason.trim() }; });
    var send;
    if (local) {
      var g = localJudge(compare, key, judge);
      send = Promise.resolve({
        answers: { judge: g.judge, keyedCorrect: g.keyedCorrect, keyedTotal: g.keyedTotal, correct: true },
        judgeAnswers: key ? key.judgeAnswers : null,
        modelReasons: key ? key.modelReasons : null,
        predictAnswers: key ? key.predictAnswers : null
      });
    } else {
      send = post({ action: 'submit', runs: unsentRuns(), judge: judge });
    }
    send.then(function (reply) {
      var view = reply && reply.answers;
      if (!view || view.correct !== true) { throw new Error('not-accepted'); }
      S.submitted = true;
      S.submittedView = view;
      S.judgeAnswers = reply.judgeAnswers || null;
      S.modelReasons = reply.modelReasons || null;
      if (Array.isArray(reply.predictAnswers)) { S.predictAnswers = reply.predictAnswers; S.revealShown = true; }
      safeStorage(function (ls) { ls.removeItem(draftKey); });
      judgeStatus.textContent = '';
      renderSubmitted();
      renderReveal();
      announce('Your judgement is in.');
      var h = doc.getElementById('cmp-results-heading');
      if (h) { h.focus(); }
      // one-tab-same-tab-launches: the one success moment — a student's accepted submit. Never
      // when staff or a preview evaluated locally (nothing was posted), never after a failed POST
      // (that throws past here into the catch), and never on load (only this handler reaches it).
      if (!local && !previewMode && cfg.staff !== true) {
        offerClose({ sameTab: true, label: 'Nice work — you made your judgement.' });
      }
    }).catch(function (err) {
      console.error(err);
      judgeStatus.textContent = '';
      judgeError.textContent = (err && err.status === 400 && err.message && err.message.indexOf('respond-') !== 0)
        ? err.message
        : 'Your judgement could not be sent. Check your connection and try again.';
      judgeError.hidden = false;
    }).then(function () {
      S.busy = false;
      update();
    });
  });

  // ── first render ───────────────────────────────────────────────────────────────────────
  if (S.predictionsRecorded && predictStatus) {
    predictStatus.textContent = S.revealShown ? '' : 'Prediction saved — run the code to find out.';
  }
  runNextBtn.addEventListener('click', function () { selectTab(runNextBtn.dataset.next); });
  if (sweepNextBtn) { sweepNextBtn.addEventListener('click', function () { selectTab('judge'); }); }
  activeTab = S.submitted ? TABS[TABS.length - 1]
    : (questions.length && !S.predictionsRecorded) ? TABS[0] : TABS[questions.length ? 1 : 0];
  versions.forEach(function (v, vi) { renderPanel(vi); });
  renderBadges();
  renderReveal();
  renderTable();
  renderSubmitted();
  update();

  // Warm the hidden runner once the visible one is up, so the two Pyodide loads never compete.
  core.on('ready', function () { ensureHidden().catch(function () { /* shown in the panel */ }); });
}
